import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useState, type ReactNode } from "react";

import { ApiTokensCard } from "@/components/ApiTokensCard";
import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/lib/AuthContext";

const INPUT_CLS = "w-full px-3 py-2 rounded-md bg-surface border border-border";

export default function Settings() {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Admin-section cards moved to the dedicated «Админ» link in the top
  // nav. /settings is intentionally focused on personal scope: profile,
  // password, session.

  return (
    <>
      <PageHeader
        eyebrow="settings"
        title={t("settings.title")}
        description={t("settings.description")}
      />

      <Group title={t("settings.profile")}>
        <ProfileCard />
        {user?.login && <ChangePasswordCard />}
      </Group>

      <Group title={t("apiTokens.sectionTitle")}>
        <ApiTokensCard />
      </Group>

      <Group title={t("settings.session")}>
        <LogoutCard />
      </Group>
    </>
  );
}

// --- Sections ----------------------------------------------------------------

function ProfileCard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  if (!user) return null;
  return (
    <section className="card p-5 space-y-3">
      <h3 className="text-base">{user.full_name || user.name}</h3>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <Kv k="Login" v={user.login ? <span className="font-mono">{user.login}</span> : "—"} />
        <Kv k={t("settings.role")} v={<span className="pill pill-accent">{user.role}</span>} />
        <Kv k="ID" v={<span className="font-mono text-xs">{user.id}</span>} />
        <Kv
          k={t("settings.lastLogin")}
          v={user.last_login_at ? new Date(user.last_login_at).toLocaleString() : "—"}
        />
      </div>
      {user.permissions && user.permissions.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-text-muted font-mono uppercase tracking-wider">
            Permissions ({user.permissions.length})
          </summary>
          <div className="mt-2 flex flex-wrap gap-1">
            {user.permissions.map((p) => (
              <span key={p} className="pill text-xs font-mono">{p}</span>
            ))}
          </div>
        </details>
      )}
    </section>
  );
}

function ChangePasswordCard() {
  const { t } = useTranslation();
  const { changePassword } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null); setMsg(null);
    if (next.length < 8) return setErr(t("settings.errMin8"));
    if (next !== confirm) return setErr(t("changePassword.errMismatch"));
    if (next === current) return setErr(t("changePassword.errSame"));
    setBusy(true);
    try {
      await changePassword(current, next);
      setMsg(t("settings.passwordUpdated"));
      setTimeout(() => nav("/login", { replace: true }), 800);
    } catch (exc) {
      setErr(exc instanceof Error ? exc.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card p-5 space-y-4">
      <h3 className="text-base">{t("changePassword.title")}</h3>
      <p className="text-xs text-text-muted">
        {t("settings.changePasswordHint")}
      </p>
      <form onSubmit={submit} className="space-y-3">
        <Field label={t("changePassword.currentPassword")}>
          <input type="password" className={INPUT_CLS} autoComplete="current-password"
            value={current} onChange={(e) => setCurrent(e.target.value)} />
        </Field>
        <Field label={t("settings.newPasswordMin8")}>
          <input type="password" className={INPUT_CLS} autoComplete="new-password"
            value={next} onChange={(e) => setNext(e.target.value)} />
        </Field>
        <Field label={t("changePassword.confirm")}>
          <input type="password" className={INPUT_CLS} autoComplete="new-password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}
        {msg && <p className="text-text-secondary text-sm">{msg}</p>}
        <button type="submit" className="btn btn-primary"
          disabled={busy || !current || !next || !confirm}>
          {busy ? t("changePassword.changing") : t("changePassword.submit")}
        </button>
      </form>
    </section>
  );
}

function LogoutCard() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const nav = useNavigate();
  return (
    <section className="card p-5 space-y-3">
      <p className="text-sm text-text-secondary">
        {t("settings.logoutHint")}
      </p>
      <button
        className="btn btn-danger"
        onClick={async () => { await logout(); nav("/login", { replace: true }); }}
      >
        {t("settings.logout")}
      </button>
    </section>
  );
}

// --- Layout helpers ----------------------------------------------------------

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-xs font-mono uppercase tracking-wider text-text-muted">{title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Kv({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-text-muted text-xs uppercase tracking-wider font-mono">{k}</span>
      <span className="text-text-primary">{v}</span>
    </div>
  );
}
